// covered-by: frontends/carrie/src/config/__tests__/askV2InPlace.test.ts
/**
 * In-place V2 introduction: login stays on app.puntt.ai. After PostHog
 * `ask-puntt-v2` is known, persist a marker and reload so the host gate
 * mounts the V2 tree on the same origin (same localStorage JWT).
 */
import {
  isAskSurface,
  isAskSurfaceLockedByHostOrOverride,
  persistInPlaceAskSurface,
} from "./askSurface";
import { BASEPATH, POSTHOG_KEY } from "./env";
import { isFlagOn } from "./featureFlags";
import { posthogClient } from "./posthog";

export const FLAG_WAIT_MS = 4000;
const ASK_V2_FLAG = "ask-puntt-v2";

const IN_PLACE_PATHS = [
  /^\/$/,
  /^\/login\/?$/,
  /^\/forgot-password\/?$/,
  /^\/reset-password\/?$/,
  /^\/ask-puntt(?:\/|$)/,
];

/** Epic deploys serve Carrie under VITE_BASEPATH (e.g. /ask-v2-surface). */
export function pathnameWithoutBase(
  pathname: string,
  basepath = BASEPATH,
): string {
  const path = pathname.split("?")[0] || "/";
  const base =
    !basepath || basepath === "/" ? "" : basepath.replace(/\/+$/, "");
  if (base && (path === base || path.startsWith(`${base}/`))) {
    return path.slice(base.length) || "/";
  }
  return path.startsWith("/") ? path : `/${path}`;
}

export function isAskV2InPlaceEligiblePath(
  pathname: string,
  basepath = BASEPATH,
): boolean {
  const path = pathnameWithoutBase(pathname, basepath);
  return IN_PLACE_PATHS.some((re) => re.test(path));
}

function hasDevFlagOverride(): boolean {
  if (!import.meta.env.DEV || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(`ff:${ASK_V2_FLAG}`) != null;
  } catch {
    return false;
  }
}

let flagPromise: Promise<boolean> | null = null;

export function resetAskV2FlagWaitForTests(): void {
  flagPromise = null;
}

/**
 * Resolves with the current `ask-puntt-v2` value once PostHog has evaluated
 * flags (or immediately when PostHog is not connected). Caps the wait so
 * login cannot hang if identify is slow. Repeat callers share one wait.
 */
export function waitForAskV2Flag(): Promise<boolean> {
  if (flagPromise) return flagPromise;
  flagPromise = loadAskV2Flag();
  return flagPromise;
}

function loadAskV2Flag(): Promise<boolean> {
  if (!POSTHOG_KEY || hasDevFlagOverride()) {
    return Promise.resolve(isFlagOn(ASK_V2_FLAG));
  }

  return new Promise((resolve) => {
    let settled = false;
    let unsubscribe: (() => void) | undefined;
    const timer: { id?: number } = {};

    const finish = () => {
      if (settled) return;
      settled = true;
      unsubscribe?.();
      if (timer.id != null && typeof window !== "undefined") {
        window.clearTimeout(timer.id);
      }
      resolve(isFlagOn(ASK_V2_FLAG));
    };

    try {
      const maybeUnsub = posthogClient?.onFeatureFlags(finish);
      if (typeof maybeUnsub === "function") unsubscribe = maybeUnsub;
    } catch {
      finish();
      return;
    }

    if (typeof window === "undefined") {
      finish();
      return;
    }
    timer.id = window.setTimeout(finish, FLAG_WAIT_MS);
  });
}

export type ApplyAskV2InPlaceReloadDeps = {
  mountedAskSurface?: boolean;
  hostLocked?: boolean;
  basepath?: string;
  replace?: (url: string) => void;
};

/** Reload target that respects the epic BASEPATH (e.g. /ask-v2-surface/). */
export function inPlaceReloadTarget(basepath = BASEPATH): string {
  const base =
    !basepath || basepath === "/" ? "" : basepath.replace(/\/+$/, "");
  return `${base}/`;
}

/**
 * Persist the in-place marker only when this origin is about to reload.
 * Host-locked Ask hosts never write the marker — it would stick on the
 * same origin after a local `?surface=ask` / env override is dropped.
 * A failed storage write never reloads — that looped forever in private mode.
 */
// why: router tree is chosen at module load — flags arrive after identify, so a reload is the only same-origin swap that keeps V1/V2 from mixing
export function applyAskV2InPlaceReload(
  flagOn: boolean,
  deps: ApplyAskV2InPlaceReloadDeps = {},
): boolean {
  const mountedAskSurface = deps.mountedAskSurface ?? isAskSurface();
  const hostLocked = deps.hostLocked ?? isAskSurfaceLockedByHostOrOverride();
  if (hostLocked) return false;
  if (Boolean(flagOn) === mountedAskSurface) return false;
  if (!persistInPlaceAskSurface(flagOn)) return false;

  const replace =
    deps.replace ??
    ((url: string) => {
      window.location.replace(url);
    });
  replace(inPlaceReloadTarget(deps.basepath));
  return true;
}
